import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { Stack, styled } from '@mui/material';
import { Children, FC, PropsWithChildren } from 'react';
import { Autoplay, FreeMode, Grid, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { PaginationOptions, SwiperOptions } from 'swiper/types';

const CarouselWrapper = styled(Stack)<{ swiperdirection?: 'vertical' | 'horizontal' }>(
  ({ theme, swiperdirection }) => ({
    position: 'relative',
    width: '100%',
    '& .swipper-custom-pagination': {
      width: 8,
      height: 8,
      backgroundColor: theme.palette.grey[300],
      opacity: 'unset',
    },
    '& .swiper-pagination-bullet-active': {
      width: 24,
      backgroundColor: theme.palette.background.paper,
      borderRadius: '16px',
    },
    '& div.swiper-grid-column .swiper-wrapper': {
      '& .swiper-slide': {
        height: 'unset',
      },
    },
    '& .swipper': {
      width: '100%',
      height: '100%',
      '& .swiper-button-next': {
        ...(swiperdirection === 'vertical'
          ? {
              height: 24,
              width: '100%',
              padding: theme.spacing(0, 2.5),
              backgroundColor: theme.palette.common.white,
              top: '100%',
              right: 0,
              left: 0,
            }
          : {
              width: 40,
              height: 40,
              backgroundColor: theme.palette.grey[100],
              borderRadius: '100%',
              border: `1px solid ${theme.palette.grey[300]}`,
              padding: theme.spacing(2),
              right: 16,
            }),

        '&::after': {
          ...theme.typography.h5,
          fontFamily: 'unicons-line',
          color: theme.palette.grey[300],
          ...(swiperdirection === 'vertical'
            ? {
                content: "'\\eb3a'",
              }
            : {
                content: '"\\eb79"',
              }),
        },
        '&:hover': {
          opacity: 0.7,
        },
      },
      '& .swiper-button-prev': {
        ...(swiperdirection === 'vertical'
          ? {
              height: 24,
              width: '100%',
              padding: theme.spacing(0, 2.5),
              backgroundColor: theme.palette.common.white,
              top: 22,
              right: 0,
              left: 0,
            }
          : {
              width: 40,
              height: 40,
              backgroundColor: theme.palette.grey[100],
              borderRadius: '100%',
              border: `1px solid ${theme.palette.grey[300]}`,
              padding: theme.spacing(2),
              left: 16,
            }),
        '&::after': {
          ...theme.typography.h5,
          fontFamily: 'unicons-line',
          color: theme.palette.grey[300],
          ...(swiperdirection === 'vertical'
            ? {
                content: '"\\eb56"',
              }
            : {
                content: "'\\eb6d'",
              }),
        },
        '&:hover': {
          opacity: 0.7,
        },
      },
      '& .swiper-button-disabled': {
        display: 'none',
      },
      '& .swiper-button-disabled.swiper-button-lock': {
        display: 'none ',
      },
    },
  }),
);

export interface IHBCarouselProps extends SwiperOptions, SwiperProps {
  pagination?: PaginationOptions;
  style?: React.CSSProperties;
  delay?: number;
}

export const HBCarousel: FC<PropsWithChildren<IHBCarouselProps>> = (props) => {
  const {
    children,
    freeMode,
    style,
    direction = 'horizontal',
    navigation = true,
    delay,
    ...rest
  } = props;

  return (
    <CarouselWrapper swiperdirection={direction} direction="row" alignItems="center" style={style}>
      <Swiper
        modules={[Navigation, Pagination, FreeMode, Autoplay, Grid]}
        freeMode={freeMode}
        navigation={navigation}
        dir="rtl"
        direction={direction}
        className="swipper"
        speed={delay ? 500 : undefined}
        autoplay={
          delay
            ? {
                delay: delay * 1000,
                pauseOnMouseEnter: true,
                disableOnInteraction: false,
              }
            : undefined
        }
        {...rest}
      >
        {Children.map(children, (child, index) => (
          <SwiperSlide style={{ ...(freeMode && { width: 'auto' }) }} key={index}>
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
    </CarouselWrapper>
  );
};
